export const economicsPrinciplesCourse = {
  id: "economics-principles-2024",
  title: "Economics Principles: Markets, Behavior & Policy",
  shortDescription: "Understand fundamental economic concepts, market mechanisms, and the role of economics in society.",
  description: "Comprehensive introduction to economic principles covering microeconomics, macroeconomics, market structures, and economic policy. Students will analyze how individuals, businesses, and governments make economic decisions and how these decisions affect society.",
  category: "Social Sciences",
  industryCategory: "Social Sciences",
  difficulty: "beginner",
  duration: 15,
  estimatedWeeks: 15,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from economics professors and policy analysts",
  thumbnailUrl: "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),

  learningObjectives: [
    "Understand fundamental economic principles and terminology",
    "Analyze supply and demand relationships",
    "Evaluate different market structures and their effects",
    "Examine the role of government in the economy",
    "Understand macroeconomic indicators and policies",
    "Analyze economic decision-making at individual and societal levels",
    "Evaluate the impact of economic policies on different groups",
    "Apply economic reasoning to contemporary issues"
  ],

  prerequisites: [
    "High school education",
    "Basic math skills",
    "Interest in understanding how economies work"
  ],

  skills: [
    "Economic Analysis",
    "Market Analysis",
    "Policy Evaluation",
    "Data Interpretation",
    "Critical Thinking",
    "Graph Analysis",
    "Research Skills",
    "Problem Solving"
  ],

  marketData: {
    averageSalary: 62000,
    salaryRange: {
      min: 45000,
      max: 85000
    },
    jobDemand: 3.8,
    marketTrend: "stable",
    careerPaths: [
      "Economic Analyst",
      "Policy Analyst",
      "Financial Analyst",
      "Market Research Analyst",
      "Business Consultant",
      "Government Economist"
    ],
    relatedSkills: [
      "Economics",
      "Data Analysis",
      "Statistics",
      "Policy Analysis",
      "Research",
      "Financial Modeling"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "economic-fundamentals",
      title: "Economic Fundamentals",
      description: "Core economic concepts including scarcity, opportunity cost, and economic systems",
      order: 1,
      estimatedHours: 4.0,
      content: [
        {
          id: "economics-intro",
          title: "What is Economics?",
          description: "Introduction to economics as the study of choice under scarcity",
          type: "video",
          duration: 30
        },
        {
          id: "economic-principles",
          title: "Fundamental Economic Principles",
          description: "Scarcity, opportunity cost, and rational decision-making",
          type: "text",
          duration: 45,
          textContent: `# Fundamental Economic Principles

## Understanding Economics

### Defining Economics

Economics is the social science that studies how individuals, businesses, and societies make choices about allocating scarce resources to satisfy unlimited wants and needs.

**Key Characteristics:**
- **Scarcity**: Limited resources relative to unlimited wants
- **Choice**: Decisions about resource allocation
- **Trade-offs**: Giving up one thing to get another
- **Efficiency**: Getting the most from available resources

### The Economic Problem

**Scarcity**
All societies face the fundamental problem of scarcity - having limited resources but unlimited wants and needs.

**Three Basic Economic Questions:**
1. **What to produce?** Which goods and services should be produced?
2. **How to produce?** What production methods should be used?
3. **For whom to produce?** How should goods and services be distributed?

### Opportunity Cost

**Definition**
Opportunity cost is the value of the next best alternative that must be given up when making a choice.

**Examples:**
- **Individual**: Choosing to study instead of working means giving up wages
- **Business**: Investing in new equipment means less money for advertising
- **Government**: Spending on defense means less available for education

**Production Possibilities Frontier (PPF)**
A graphical representation showing the maximum combination of goods that can be produced with available resources.

**Key Concepts:**
- **Points on the curve**: Efficient production
- **Points inside the curve**: Inefficient use of resources
- **Points outside the curve**: Currently impossible with available resources
- **Slope**: Represents opportunity cost

### Economic Systems

**Market Economy (Capitalism)**
- **Private ownership** of resources and means of production
- **Price mechanism** coordinates economic activity
- **Profit motive** drives decision-making
- **Competition** promotes efficiency
- **Examples**: United States, Hong Kong

**Command Economy (Socialism)**
- **Government ownership** of major resources
- **Central planning** coordinates economic activity
- **Social goals** rather than profit motive
- **Government control** of production and distribution
- **Examples**: Cuba, North Korea

**Mixed Economy**
- **Combination** of market and command elements
- **Private and public sectors** coexist
- **Government regulation** of markets
- **Social safety net** alongside market mechanisms
- **Examples**: Most modern economies (Sweden, Germany, Canada)

### Microeconomics vs. Macroeconomics

**Microeconomics**
- Studies individual economic units
- **Focus**: Households, firms, markets
- **Topics**: Supply and demand, consumer choice, firm behavior
- **Questions**: Why do prices change? How do firms make production decisions?

**Macroeconomics**
- Studies the economy as a whole
- **Focus**: National economy, economic aggregates
- **Topics**: GDP, unemployment, inflation, economic growth
- **Questions**: What causes economic growth? How can unemployment be reduced?

### Economic Models and Assumptions

**Economic Models**
Simplified representations of economic reality used to understand and predict economic behavior.

**Key Assumptions:**
- **Rational behavior**: People make decisions to maximize their well-being
- **Self-interest**: Individuals act in their own best interest
- **Perfect information**: Access to all relevant information
- **Ceteris paribus**: "All other things being equal"

**Limitations of Models:**
- Simplified view of complex reality
- Assumptions may not hold in practice
- Human behavior can be irrational
- External factors can change outcomes

### Economic Efficiency

**Allocative Efficiency**
Resources are allocated to produce the combination of goods and services most wanted by society.

**Productive Efficiency**
Goods and services are produced at the lowest possible cost using the fewest resources.

**Types of Efficiency:**
- **Technical efficiency**: Maximum output from given inputs
- **Economic efficiency**: Optimal allocation of resources
- **Dynamic efficiency**: Innovation and improvement over time

### Market Failures

**Situations where markets don't allocate resources efficiently:**
- **Monopoly power**: Single seller controls market
- **Public goods**: Non-excludable and non-rivalrous goods
- **Externalities**: Costs or benefits affecting third parties
- **Information asymmetries**: Unequal access to information

### Role of Government

**Government functions in the economy:**
- **Regulation**: Ensuring fair competition and safety
- **Redistribution**: Reducing income inequality
- **Stabilization**: Managing economic fluctuations
- **Public goods provision**: Providing goods markets won't supply

Understanding these fundamental principles provides the foundation for analyzing more complex economic phenomena and policy issues.`
        },
        {
          id: "economic-decision-making",
          title: "Economic Decision Making Exercise",
          description: "Practice applying economic principles to real-world scenarios",
          type: "social-sciences-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "economic-analysis",
            instructions: "Analyze economic scenarios using fundamental principles like opportunity cost, scarcity, and efficiency.",
            scenarios: [
              {
                id: "college-decision",
                situation: "A high school graduate must decide between going to college or starting work immediately",
                factors: ["College tuition costs", "Lost wages during college", "Higher future earnings", "Job satisfaction"],
                concepts: ["Opportunity cost", "Investment in human capital", "Present vs. future value"]
              },
              {
                id: "government-spending",
                situation: "A city government must choose between building a new hospital or improving public transportation",
                factors: ["Public health benefits", "Economic development", "Construction costs", "Ongoing maintenance"],
                concepts: ["Public goods", "Opportunity cost", "Social benefit-cost analysis"]
              }
            ],
            hints: [
              "Consider both immediate and long-term costs and benefits",
              "Think about who is affected by each decision",
              "Remember that every choice involves giving up alternatives"
            ]
          }
        }
      ],
      quiz: {
        id: "economic-fundamentals-quiz",
        title: "Economic Fundamentals Quiz",
        description: "Test your understanding of basic economic concepts",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Opportunity cost is:",
            options: [
              "The monetary cost of a decision",
              "The value of the next best alternative given up",
              "The total cost of all alternatives",
              "The difference between costs and benefits"
            ],
            correctAnswer: 1,
            explanation: "Opportunity cost is the value of the next best alternative that must be given up when making a choice."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Which is NOT one of the three basic economic questions?",
            options: [
              "What to produce?",
              "How to produce?",
              "When to produce?",
              "For whom to produce?"
            ],
            correctAnswer: 2,
            explanation: "The three basic economic questions are: What to produce? How to produce? For whom to produce? 'When to produce' is not one of the fundamental questions."
          }
        ]
      }
    },

    {
      id: "supply-and-demand",
      title: "Supply and Demand",
      description: "Market forces that determine prices and quantities in competitive markets",
      order: 2,
      estimatedHours: 4.0,
      content: [
        {
          id: "demand-analysis",
          title: "Understanding Demand",
          description: "Consumer behavior and factors affecting demand for goods and services",
          type: "video",
          duration: 35
        },
        {
          id: "supply-analysis",
          title: "Understanding Supply",
          description: "Producer behavior and factors affecting supply of goods and services",
          type: "text",
          duration: 45,
          textContent: `# Supply and Demand Analysis\n\n## The Law of Demand\n\n### Basic Principle\nAs price increases, quantity demanded decreases, all else being equal (ceteris paribus).\n\n### Demand Curve\n- **Downward Sloping**: Shows inverse relationship between price and quantity demanded\n- **Individual vs. Market Demand**: Sum of all individual demand curves\n- **Movement vs. Shift**: Change in quantity demanded vs. change in demand\n\n### Factors Affecting Demand\n- **Income**: Normal goods (positive relationship), inferior goods (negative relationship)\n- **Price of Related Goods**: Substitutes (positive cross-price elasticity), complements (negative cross-price elasticity)\n- **Consumer Preferences**: Tastes, advertising, trends\n- **Number of Consumers**: Market size effects\n- **Future Expectations**: Expected price or income changes\n\n## The Law of Supply\n\n### Basic Principle\nAs price increases, quantity supplied increases, all else being equal.\n\n### Supply Curve\n- **Upward Sloping**: Shows positive relationship between price and quantity supplied\n- **Individual vs. Market Supply**: Sum of all individual supply curves\n- **Movement vs. Shift**: Change in quantity supplied vs. change in supply\n\n### Factors Affecting Supply\n- **Production Costs**: Input prices, technology, productivity\n- **Number of Sellers**: Market competition and entry/exit\n- **Government Policies**: Taxes, subsidies, regulations\n- **Future Expectations**: Expected price changes\n- **Weather/Natural Conditions**: Especially for agricultural products\n\n## Market Equilibrium\n\n### Equilibrium Point\n- **Definition**: Where quantity demanded equals quantity supplied\n- **Market Clearing**: No shortage or surplus at equilibrium price\n- **Stability**: Market forces push toward equilibrium\n\n### Changes in Equilibrium\n- **Demand Increase**: Higher equilibrium price and quantity\n- **Supply Increase**: Lower equilibrium price, higher equilibrium quantity\n- **Simultaneous Changes**: Combined effects on price and quantity\n\n## Elasticity of Demand and Supply\n\n### Price Elasticity of Demand\n- **Formula**: % Change in Quantity Demanded / % Change in Price\n- **Elastic Demand**: |Ed| > 1 (responsive to price changes)\n- **Inelastic Demand**: |Ed| < 1 (less responsive to price changes)\n- **Unit Elastic**: |Ed| = 1\n\n### Factors Affecting Price Elasticity\n- **Availability of Substitutes**: More substitutes = more elastic\n- **Necessity vs. Luxury**: Necessities tend to be inelastic\n- **Time Period**: Longer time allows more adjustment\n- **Proportion of Income**: Larger budget share = more elastic\n\n### Price Elasticity of Supply\n- **Short Run**: Limited ability to adjust production\n- **Long Run**: More flexibility in production adjustments\n- **Factors**: Production technology, input availability, time to adjust\n\nUnderstanding supply and demand is fundamental to analyzing how markets work and how prices are determined.`
        }
      ],
      quiz: {
        id: "supply-demand-quiz",
        title: "Supply and Demand Quiz",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "If the price of coffee increases, what happens to the demand for tea (assuming they are substitutes)?",
            options: ["Demand decreases", "Demand increases", "Demand stays the same", "Supply increases"],
            correctAnswer: 1,
            explanation: "When the price of a substitute good (coffee) increases, demand for the alternative good (tea) increases as consumers switch to the relatively cheaper option."
          }
        ]
      }
    },

    {
      id: "market-structures",
      title: "Market Structures",
      description: "Perfect competition, monopoly, oligopoly, and monopolistic competition",
      order: 3,
      estimatedHours: 4.0,
      content: [
        {
          id: "competition-types",
          title: "Types of Market Competition",
          description: "Characteristics and behavior in different market structures",
          type: "video",
          duration: 30
        },
        {
          id: "market-efficiency",
          title: "Market Efficiency and Welfare",
          description: "How different market structures affect economic efficiency",
          type: "text",
          duration: 45,
          textContent: `# Market Structures and Efficiency\n\n## Perfect Competition\n\n### Characteristics\n- **Many Buyers and Sellers**: No single participant can influence market price\n- **Homogeneous Products**: Identical goods from all producers\n- **Perfect Information**: All participants know prices and quality\n- **Free Entry and Exit**: No barriers to entering or leaving market\n- **Price Takers**: Firms accept market price\n\n### Economic Outcomes\n- **Economic Efficiency**: Resources allocated optimally\n- **Zero Economic Profit**: Long-run equilibrium condition\n- **Consumer Surplus Maximized**: Lowest possible prices\n- **Innovation Incentives**: Limited due to profit constraints\n\n## Monopoly\n\n### Characteristics\n- **Single Seller**: One firm controls entire market\n- **No Close Substitutes**: Unique product with no alternatives\n- **High Barriers to Entry**: Legal, technological, or economic obstacles\n- **Price Maker**: Firm can set price above marginal cost\n\n### Sources of Monopoly Power\n- **Legal Monopolies**: Patents, copyrights, government franchises\n- **Natural Monopolies**: High fixed costs make single firm most efficient\n- **Resource Control**: Exclusive access to essential inputs\n- **Network Effects**: Value increases with number of users\n\n### Economic Outcomes\n- **Allocative Inefficiency**: Price above marginal cost\n- **Productive Inefficiency**: May not minimize costs\n- **Deadweight Loss**: Reduced total economic welfare\n- **Monopoly Profit**: Economic profit in long run\n\n## Oligopoly\n\n### Characteristics\n- **Few Large Firms**: Small number of dominant sellers\n- **Interdependence**: Firms consider rivals' reactions\n- **Barriers to Entry**: Significant obstacles for new firms\n- **Product Differentiation**: May be homogeneous or differentiated\n\n### Strategic Behavior\n- **Game Theory**: Strategic decision-making\n- **Collusion**: Cooperation to restrict output and raise prices\n- **Price Leadership**: One firm sets price, others follow\n- **Non-Price Competition**: Advertising, quality, service\n\n### Models of Oligopoly\n- **Cournot Model**: Firms compete on quantity\n- **Bertrand Model**: Firms compete on price\n- **Stackelberg Model**: Sequential decision-making\n- **Kinked Demand Curve**: Explains price rigidity\n\n## Monopolistic Competition\n\n### Characteristics\n- **Many Firms**: Large number of competitors\n- **Product Differentiation**: Similar but not identical products\n- **Some Market Power**: Ability to set price above marginal cost\n- **Free Entry and Exit**: Low barriers to entry\n\n### Differentiation Strategies\n- **Physical Differences**: Quality, design, features\n- **Location**: Convenience and accessibility\n- **Brand Image**: Marketing and reputation\n- **Service**: Customer support and experience\n\n### Economic Outcomes\n- **Excess Capacity**: Firms produce below minimum efficient scale\n- **Markup Pricing**: Price above marginal cost\n- **Zero Economic Profit**: Long-run entry eliminates profits\n- **Consumer Choice**: Variety of products available\n\n## Comparing Market Structures\n\n### Efficiency Comparison\n- **Perfect Competition**: Most efficient allocation\n- **Monopolistic Competition**: Some inefficiency due to differentiation\n- **Oligopoly**: Varies depending on competition level\n- **Monopoly**: Least efficient allocation\n\n### Innovation and Progress\n- **Monopoly**: May have resources but limited incentives\n- **Perfect Competition**: Limited resources for R&D\n- **Oligopoly**: Balanced incentives and resources\n- **Monopolistic Competition**: Innovation in differentiation\n\n### Policy Implications\n- **Antitrust Laws**: Preventing monopolization and collusion\n- **Regulation**: Natural monopoly oversight\n- **Patent System**: Balancing innovation incentives and competition\n- **Market Contestability**: Reducing entry barriers\n\nUnderstanding market structures helps explain firm behavior, pricing strategies, and economic outcomes in different competitive environments.`
        }
      ],
      quiz: {
        id: "market-structures-quiz",
        title: "Market Structures Quiz",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which market structure is characterized by many firms selling differentiated products?",
            options: ["Perfect competition", "Monopoly", "Oligopoly", "Monopolistic competition"],
            correctAnswer: 3,
            explanation: "Monopolistic competition is characterized by many firms selling similar but differentiated products, allowing each firm some pricing power."
          }
        ]
      }
    },

    {
      id: "macroeconomic-indicators",
      title: "Macroeconomic Indicators",
      description: "GDP, inflation, unemployment, and other measures of economic performance",
      order: 4,
      estimatedHours: 4.0,
      content: [
        {
          id: "gdp-measurement",
          title: "Gross Domestic Product",
          description: "Measuring total economic output and economic growth",
          type: "video",
          duration: 35
        }
      ],
      quiz: {
        id: "macro-indicators-quiz",
        title: "Macroeconomic Indicators Quiz",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "GDP measures:",
            options: ["Total income in an economy", "Total production in an economy", "Total wealth in an economy", "Total employment in an economy"],
            correctAnswer: 1,
            explanation: "GDP (Gross Domestic Product) measures the total value of all final goods and services produced in an economy during a specific period."
          }
        ]
      }
    },

    {
      id: "monetary-fiscal-policy",
      title: "Monetary and Fiscal Policy",
      description: "Government and central bank tools for managing the economy",
      order: 5,
      estimatedHours: 4.0,
      content: [
        {
          id: "monetary-policy",
          title: "Central Bank and Monetary Policy",
          description: "Interest rates, money supply, and economic stabilization",
          type: "video",
          duration: 30
        }
      ],
      quiz: {
        id: "policy-quiz",
        title: "Monetary and Fiscal Policy Quiz",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Expansionary fiscal policy involves:",
            options: ["Increasing taxes and reducing spending", "Decreasing taxes and increasing spending", "Increasing interest rates", "Decreasing money supply"],
            correctAnswer: 1,
            explanation: "Expansionary fiscal policy involves decreasing taxes and/or increasing government spending to stimulate economic growth."
          }
        ]
      }
    },

    {
      id: "international-economics",
      title: "International Economics",
      description: "Trade, exchange rates, and global economic relationships",
      order: 6,
      estimatedHours: 4.0,
      content: [
        {
          id: "trade-theory",
          title: "International Trade Theory",
          description: "Comparative advantage and gains from trade",
          type: "video",
          duration: 35
        }
      ],
      quiz: {
        id: "international-economics-quiz",
        title: "International Economics Quiz",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Comparative advantage means:",
            options: ["Being better at everything", "Having lower opportunity cost", "Having more resources", "Being more efficient"],
            correctAnswer: 1,
            explanation: "Comparative advantage means having a lower opportunity cost in producing a good compared to other producers."
          }
        ]
      }
    },

    {
      id: "labor-economics",
      title: "Labor Economics",
      description: "Labor markets, wages, and employment economics",
      order: 7,
      estimatedHours: 4.0,
      content: [
        {
          id: "labor-markets",
          title: "Labor Market Analysis",
          description: "How wages and employment are determined",
          type: "video",
          duration: 30
        }
      ],
      quiz: {
        id: "labor-economics-quiz",
        title: "Labor Economics Quiz",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What determines wages in competitive labor markets?",
            options: ["Government minimum wage laws", "Supply and demand for labor", "Company profit levels", "Worker negotiations"],
            correctAnswer: 1,
            explanation: "In competitive labor markets, wages are primarily determined by the interaction of labor supply and labor demand."
          }
        ]
      }
    },

    {
      id: "economic-growth-development",
      title: "Economic Growth and Development",
      description: "Long-term economic growth and development economics",
      order: 8,
      estimatedHours: 4.0,
      content: [
        {
          id: "growth-theory",
          title: "Theories of Economic Growth",
          description: "Factors driving long-term economic growth and development",
          type: "video",
          duration: 35
        }
      ],
      quiz: {
        id: "growth-development-quiz",
        title: "Economic Growth and Development Quiz",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which factor is most important for long-term economic growth?",
            options: ["Natural resources", "Labor force size", "Technological progress", "Government spending"],
            correctAnswer: 2,
            explanation: "Technological progress is generally considered the most important factor for sustained long-term economic growth."
          }
        ]
      }
    }
  ],

  assignments: [
    {
      id: "economic-analysis-project",
      title: "Economic Policy Analysis Project",
      description: "Analyze a current economic policy using economic principles and theories",
      type: "project",
      dueWeeks: 10,
      maxScore: 100,
      instructions: "Choose a current economic policy (minimum wage, trade policy, environmental regulation, etc.) and analyze its economic effects using concepts learned in class. Consider costs, benefits, and distributional effects.",
      submissionFormat: "pdf",
      resources: [
        "Economic policy databases",
        "Government economic reports",
        "Academic economics journals",
        "Statistical data sources"
      ]
    }
  ],

  finalExam: {
    id: "economics-final-exam",
    title: "Economics Principles Final Examination",
    description: "Comprehensive examination covering microeconomic and macroeconomic principles",
    timeLimit: 180,
    passingScore: 70,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "When the price of a good increases, what happens to quantity demanded according to the law of demand?",
        options: [
          "Quantity demanded increases",
          "Quantity demanded decreases",
          "Quantity demanded stays the same",
          "The relationship is unpredictable"
        ],
        correctAnswer: 1,
        explanation: "According to the law of demand, when price increases, quantity demanded decreases, all other things being equal."
      },
      {
        id: "final-q2",
        type: "multiple-choice",
        question: "What is GDP?",
        options: [
          "Government Deficit Program",
          "Gross Domestic Product",
          "General Development Plan",
          "Global Distribution Process"
        ],
        correctAnswer: 1,
        explanation: "GDP stands for Gross Domestic Product, which measures the total value of all final goods and services produced within a country in a given period."
      }
    ]
  },

  quizzes: []
};